/**
 * Auth Service — Abstraction Layer for Authentication
 *
 * Provides a unified auth interface that works in both modes:
 * - LOCAL mode: Current behavior (localStorage, client-side hashing)
 * - BACKEND mode: JWT-based auth via API (future)
 *
 * This is the SINGLE entry point for all auth operations.
 * Components should import from here, not from auth.ts directly.
 *
 * @created 22 april 2026 — Backend migration preparation (S-SCAN §14)
 */

import { AccountsState, Account } from '../types'
import {
  hashPassword,
  verifyPassword,
  findAccountByUsername,
  findAccountByEmail,
  findAccountByLinkCode,
  isSessionExpired,
  sanitizeEmail,
  isLegacyPasswordHash,
} from './auth'

// ─── Configuration ────────────────────────────────────────────

/**
 * When API_AUTH_BASE is set, auth operations go through the backend.
 * When empty, they use the current client-side localStorage flow.
 */
const API_AUTH_BASE = '' // e.g. 'https://api.meesterdjay.nl/v1/auth'

const USE_BACKEND_AUTH = Boolean(API_AUTH_BASE)

// ─── Types ────────────────────────────────────────────────────

export interface AuthTokens {
  /** Short-lived access token (JWT, ~15 min) */
  accessToken: string
  /** Long-lived refresh token (~7 days) — stored in httpOnly cookie in production */
  refreshToken: string
  /** Access token expiry timestamp (ms) */
  expiresAt: number
}

export interface AuthSession {
  accountId: string
  role: 'learner' | 'parent' | 'teacher' | 'admin'
  displayName: string
  /** Only present in backend mode */
  tokens?: AuthTokens
  /**
   * If the account was authenticated with a legacy un-prefixed password hash,
   * this contains the freshly-computed prefixed hash. Consumers should persist
   * it via `updateAccountPassword(session.accountId, session.upgradedHash)`
   * to lazily migrate to the new format. Absent on already-migrated accounts.
   */
  upgradedHash?: string
}

export interface LoginResult {
  success: boolean
  session?: AuthSession
  error?: string
  /** Error code for programmatic handling */
  errorCode?:
    | 'INVALID_CREDENTIALS'
    | 'ACCOUNT_NOT_FOUND'
    | 'ACCOUNT_LOCKED'
    | 'RATE_LIMITED'
    | 'NETWORK_ERROR'
}

export interface RegisterResult {
  success: boolean
  accountId?: string
  session?: AuthSession
  error?: string
}

// ─── Rate Limiting (client-side, persisted) ───────────────────

const MAX_LOGIN_ATTEMPTS = 5
const LOCKOUT_DURATION_MS = 30_000 // 30 seconds
const ATTEMPT_STORAGE_KEY = 'mp_login_attempts'

type AttemptRecord = { count: number; lockedUntil: number }

/**
 * Load persisted attempt-tracker from localStorage.
 * Reload-survival: a page refresh no longer resets rate-limiting.
 */
function loadAttemptTracker(): Map<string, AttemptRecord> {
  if (typeof localStorage === 'undefined') return new Map()
  try {
    const raw = localStorage.getItem(ATTEMPT_STORAGE_KEY)
    if (!raw) return new Map()
    const parsed = JSON.parse(raw) as Record<string, AttemptRecord>
    if (!parsed || typeof parsed !== 'object') return new Map()
    const map = new Map<string, AttemptRecord>()
    const now = Date.now()
    for (const [key, value] of Object.entries(parsed)) {
      // Drop expired lockouts during load (cheap GC).
      if (
        value &&
        typeof value.count === 'number' &&
        typeof value.lockedUntil === 'number' &&
        (value.lockedUntil === 0 || value.lockedUntil > now)
      ) {
        map.set(key, value)
      }
    }
    return map
  } catch {
    return new Map()
  }
}

function persistAttemptTracker(): void {
  if (typeof localStorage === 'undefined') return
  try {
    const obj: Record<string, AttemptRecord> = {}
    for (const [key, value] of attemptTracker.entries()) {
      obj[key] = value
    }
    localStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(obj))
  } catch {
    // Best-effort persistence — silent on quota errors.
  }
}

const attemptTracker: Map<string, AttemptRecord> = loadAttemptTracker()

/**
 * Check if a login identifier (email/username) is currently rate-limited.
 * Returns remaining lockout time in ms, or 0 if not locked.
 */
export function getRateLimitRemaining(identifier: string): number {
  const key = identifier.toLowerCase().trim()
  const tracker = attemptTracker.get(key)
  if (!tracker) return 0
  if (tracker.lockedUntil > Date.now()) {
    return tracker.lockedUntil - Date.now()
  }
  return 0
}

/**
 * Record a failed login attempt. Returns true if now locked out.
 */
function recordFailedAttempt(identifier: string): boolean {
  const key = identifier.toLowerCase().trim()
  const tracker = attemptTracker.get(key) ?? { count: 0, lockedUntil: 0 }

  // Reset if lockout has expired
  if (tracker.lockedUntil > 0 && tracker.lockedUntil <= Date.now()) {
    tracker.count = 0
    tracker.lockedUntil = 0
  }

  tracker.count++
  let locked = false
  if (tracker.count >= MAX_LOGIN_ATTEMPTS) {
    tracker.lockedUntil = Date.now() + LOCKOUT_DURATION_MS
    locked = true
  }

  attemptTracker.set(key, tracker)
  persistAttemptTracker()
  return locked
}

/** Reset attempt counter after successful login */
function clearAttempts(identifier: string): void {
  attemptTracker.delete(identifier.toLowerCase().trim())
  persistAttemptTracker()
}

// ─── Cryptographic Random ─────────────────────────────────────

/**
 * Cryptographically secure random number [0, 1).
 * Replaces Math.random() for security-sensitive operations.
 */
export function secureRandom(): number {
  const array = new Uint32Array(1)
  crypto.getRandomValues(array)
  return array[0] / (0xffffffff + 1)
}

/**
 * Generate a cryptographically secure random string from a character set.
 */
export function secureRandomString(length: number, charset: string): string {
  let result = ''
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(secureRandom() * charset.length))
  }
  return result
}

// ─── Login ────────────────────────────────────────────────────

/**
 * Login with email + password (parent/teacher/admin).
 *
 * Roles that login by email (parent/teacher/admin) MUST have a passwordHash;
 * a missing hash is treated as a fatal config error and rejected as
 * INVALID_CREDENTIALS. This closes the "no hash means anyone gets in" hole.
 */
export async function loginWithEmail(
  email: string,
  password: string,
  accounts: AccountsState,
): Promise<LoginResult> {
  const normalizedEmail = sanitizeEmail(email)

  // Rate limit check
  const lockRemaining = getRateLimitRemaining(normalizedEmail)
  if (lockRemaining > 0) {
    const seconds = Math.ceil(lockRemaining / 1000)
    return {
      success: false,
      error: `Te veel pogingen. Probeer het over ${seconds} seconden opnieuw.`,
      errorCode: 'RATE_LIMITED',
    }
  }

  if (USE_BACKEND_AUTH) {
    return backendLogin('/login', { email: normalizedEmail, password })
  }

  // Local mode
  const account = findAccountByEmail(accounts, normalizedEmail)
  if (!account) {
    recordFailedAttempt(normalizedEmail)
    return {
      success: false,
      error: 'E-mailadres niet gevonden.',
      errorCode: 'ACCOUNT_NOT_FOUND',
    }
  }

  // Email-login roles MUST have a passwordHash. A missing hash signals a
  // corrupt/test account; never silent-allow.
  if (!account.passwordHash) {
    recordFailedAttempt(normalizedEmail)
    return {
      success: false,
      error:
        'Dit account heeft geen wachtwoord ingesteld. Neem contact op met een beheerder.',
      errorCode: 'INVALID_CREDENTIALS',
    }
  }

  const isValid = await verifyPassword(password, account.passwordHash)
  if (!isValid) {
    const locked = recordFailedAttempt(normalizedEmail)
    return {
      success: false,
      error: locked
        ? 'Te veel pogingen. Probeer het over 30 seconden opnieuw.'
        : 'Wachtwoord is onjuist.',
      errorCode: locked ? 'RATE_LIMITED' : 'INVALID_CREDENTIALS',
    }
  }

  clearAttempts(normalizedEmail)

  // Opportunistic upgrade: re-hash legacy un-prefixed hashes on next login.
  // The caller (store action wired in AuthGuard) reads `session.upgradedHash`
  // and persists it via updateAccountPassword.
  let upgradedHash: string | undefined
  if (isLegacyPasswordHash(account.passwordHash)) {
    try {
      upgradedHash = await hashPassword(password)
    } catch {
      // If hashing fails (no crypto.subtle), skip the upgrade silently —
      // verification still succeeded so the user can log in.
    }
  }

  return {
    success: true,
    session: {
      accountId: account.id,
      role: account.role,
      displayName: account.displayName,
      ...(upgradedHash ? { upgradedHash } : {}),
    },
  }
}

/**
 * Login with username + optional password (learner).
 *
 * Learners may legitimately have no passwordHash (e.g. link-code-first
 * onboarding), so we keep the existing tolerant behavior for that role.
 */
export async function loginWithUsername(
  username: string,
  password: string,
  accounts: AccountsState,
): Promise<LoginResult> {
  const normalizedUsername = username.toLowerCase().trim()

  const lockRemaining = getRateLimitRemaining(normalizedUsername)
  if (lockRemaining > 0) {
    const seconds = Math.ceil(lockRemaining / 1000)
    return {
      success: false,
      error: `Te veel pogingen. Probeer het over ${seconds} seconden opnieuw.`,
      errorCode: 'RATE_LIMITED',
    }
  }

  if (USE_BACKEND_AUTH) {
    return backendLogin('/login', { username: normalizedUsername, password })
  }

  const account = findAccountByUsername(accounts, normalizedUsername)
  if (!account) {
    recordFailedAttempt(normalizedUsername)
    return {
      success: false,
      error: 'Gebruikersnaam niet gevonden.',
      errorCode: 'ACCOUNT_NOT_FOUND',
    }
  }

  // Non-learner roles that somehow log in by username (edge case) still
  // require a passwordHash. Learner accounts may pass without one.
  if (account.role !== 'learner' && !account.passwordHash) {
    recordFailedAttempt(normalizedUsername)
    return {
      success: false,
      error:
        'Dit account heeft geen wachtwoord ingesteld. Neem contact op met een beheerder.',
      errorCode: 'INVALID_CREDENTIALS',
    }
  }

  let upgradedHash: string | undefined
  if (account.passwordHash) {
    const isValid = await verifyPassword(password, account.passwordHash)
    if (!isValid) {
      const locked = recordFailedAttempt(normalizedUsername)
      return {
        success: false,
        error: locked
          ? 'Te veel pogingen. Probeer het over 30 seconden opnieuw.'
          : 'Wachtwoord is onjuist.',
        errorCode: locked ? 'RATE_LIMITED' : 'INVALID_CREDENTIALS',
      }
    }
    if (isLegacyPasswordHash(account.passwordHash)) {
      try {
        upgradedHash = await hashPassword(password)
      } catch {
        // Skip upgrade on hash failure; login still succeeds.
      }
    }
  }

  clearAttempts(normalizedUsername)
  return {
    success: true,
    session: {
      accountId: account.id,
      role: account.role,
      displayName: account.displayName,
      ...(upgradedHash ? { upgradedHash } : {}),
    },
  }
}

/**
 * Login with link code (learner — no password required).
 */
/**
 * ⛔ AFGESCHAFT (14 juli 2026) — inloggen met een koppelcode.
 *
 * Deze functie blijft bestaan zodat bestaande call-sites blijven compileren,
 * maar hij authenticeert niemand meer: hij geeft altijd een mislukking terug.
 *
 * WAAROM: de koppelcode is 6 tekens uit een alfabet van 33 (`randomCode.ts`),
 * dus ~30 bits entropie. Als login-credential is dat te zwak — brute-force op
 * een handvol tekens is genoeg om bij de volledige gegevens van een willekeurig
 * kind te komen: naam, groep, voortgang, toetsresultaten. Voor kinderdata is
 * dat niet verdedigbaar, ook niet met rate-limiting, want de codes zijn
 * statisch en worden bovendien door leerkrachten geprint en bewaard
 * (`BulkImport.tsx`).
 *
 * De koppelcode bewijst vanaf nu een RELATIE, niet een IDENTITEIT: een al
 * ingelogde ouder koppelt zichzelf aan een kind. Zie
 * `claimLearnerByLinkCode()` in `src/data/supabase/authService.ts` en
 * `docs/project/SUPABASE_AUTH_MIGRATION.md` §3.
 *
 * @deprecated Gebruik `loginWithUsername`. Verwijder deze functie zodra
 * `LoginScreen` geen koppelcode-tab meer heeft.
 */
export async function loginWithLinkCode(): Promise<LoginResult> {
  return {
    success: false,
    errorCode: 'INVALID_CREDENTIALS',
    error:
      'Inloggen met een koppelcode is niet meer mogelijk. Log in met je gebruikersnaam en wachtwoord.',
  }
}

// ─── Session Management ───────────────────────────────────────

/** In-memory token storage (backend mode only) */
let currentTokens: AuthTokens | null = null

/**
 * Get the current access token (backend mode).
 * Returns null in local mode.
 */
export function getAccessToken(): string | null {
  if (!USE_BACKEND_AUTH || !currentTokens) return null
  if (currentTokens.expiresAt <= Date.now()) {
    // Token expired — caller should refresh
    return null
  }
  return currentTokens.accessToken
}

/**
 * Check if the current session is valid.
 * In local mode: checks lastActive timestamp.
 * In backend mode: checks JWT expiry.
 */
export function isAuthenticated(
  lastActive: number,
  role: 'learner' | 'parent' | 'teacher' | 'admin',
): boolean {
  if (USE_BACKEND_AUTH) {
    return currentTokens !== null && currentTokens.expiresAt > Date.now()
  }
  return !isSessionExpired(lastActive, role)
}

/**
 * Refresh the access token using the refresh token.
 * Only relevant in backend mode.
 */
export async function refreshSession(): Promise<boolean> {
  if (!USE_BACKEND_AUTH || !currentTokens?.refreshToken) return false

  try {
    const response = await fetch(`${API_AUTH_BASE}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: currentTokens.refreshToken }),
      credentials: 'include', // For httpOnly cookie flow
    })

    if (!response.ok) {
      currentTokens = null
      return false
    }

    const data = await response.json()
    currentTokens = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken ?? currentTokens.refreshToken,
      expiresAt: Date.now() + (data.expiresIn ?? 900) * 1000,
    }
    return true
  } catch {
    return false
  }
}

/**
 * Logout — clear session and tokens.
 */
export async function logout(): Promise<void> {
  if (USE_BACKEND_AUTH && currentTokens) {
    try {
      await fetch(`${API_AUTH_BASE}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentTokens.accessToken}`,
        },
        credentials: 'include',
      })
    } catch {
      // Best-effort server logout
    }
  }
  currentTokens = null
}

// ─── Backend Helpers (Fase 3) ─────────────────────────────────

/**
 * Generic backend login call.
 * Used by all login methods in backend mode.
 */
async function backendLogin(
  endpoint: string,
  body: Record<string, string>,
): Promise<LoginResult> {
  try {
    const response = await fetch(`${API_AUTH_BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    })

    if (response.status === 429) {
      return {
        success: false,
        error: 'Te veel pogingen. Probeer het later opnieuw.',
        errorCode: 'RATE_LIMITED',
      }
    }

    if (response.status === 423) {
      return {
        success: false,
        error:
          'Account is tijdelijk vergrendeld. Neem contact op met je docent.',
        errorCode: 'ACCOUNT_LOCKED',
      }
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      return {
        success: false,
        error: data.message ?? 'Inloggen mislukt.',
        errorCode: 'INVALID_CREDENTIALS',
      }
    }

    const data = await response.json()
    currentTokens = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresAt: Date.now() + (data.expiresIn ?? 900) * 1000,
    }

    return {
      success: true,
      session: {
        accountId: data.accountId,
        role: data.role,
        displayName: data.displayName,
        tokens: currentTokens,
      },
    }
  } catch {
    return {
      success: false,
      error: 'Kan geen verbinding maken met de server.',
      errorCode: 'NETWORK_ERROR',
    }
  }
}
